import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, Phone, Mail, Globe, Sparkles } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [scrolled, setScrolled] = useState(false);
  const [siteIcon, setSiteIcon] = useState<string | null>(null);
  const location = useLocation();

  const navigationItems = [
    { label: 'Home', path: '/' },
    {
      label: 'About Us',
      path: '/about',
      dropdown: [
        { label: 'Our History', path: '/about/history' },
        { label: 'Vision & Mission', path: '/about/vision-mission' },
        { label: 'Diocesan Anthem', path: '/about/anthem' },
      ]
    },
    {
      label: 'Leadership',
      path: '/bishop',
      dropdown: [
        { label: 'Our Bishop', path: '/bishop' },
        { label: 'Episcopal Office', path: '/episcopal-office' },
        { label: 'Past Bishops', path: '/past-bishops' },
      ]
    },
    {
      label: 'Clergy & Parishes',
      path: '/clergy',
      dropdown: [
        { label: 'Our Clergy', path: '/clergy' },
        { label: 'Our Parishes', path: '/parishes' },
        { label: 'Archdeaconries', path: '/archdeaconries' },
        { label: 'Retired Priests', path: '/retired-priests' },
      ]
    },
    {
      label: 'Media',
      path: '/media',
      dropdown: [
        { label: 'Photo Gallery', path: '/gallery' },
        { label: 'Video Gallery', path: '/videos' },
      ]
    },
    { label: 'Events', path: '/events' },
    { label: 'News', path: '/news' },
    { label: 'Contact', path: '/contact' },
  ];

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);

    const fetchSiteIcon = async () => {
      try {
        const response = await axios.get('https://api.ibadansouthanglicandiocese.org.ng/api/v1/site_icon');
        setSiteIcon(response.data);
      } catch (error) {
        console.error('Failed to fetch site icon', error);
      }
    };

    fetchSiteIcon();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isActive = (path: string) =>
    location.pathname === path || (path !== '/' && location.pathname.startsWith(path));

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname])

  return (
    <header className="fixed top-0 left-0 right-0 z-[100] transition-all duration-500">
      {/* Top Thin Info Bar */}
      <div className="hidden bg-slate-900 text-slate-400 py-2 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 flex justify-between items-center text-[10px] font-bold uppercase tracking-widest">
          <div className="flex gap-6">
            <span className="flex items-center gap-2 hover:text-amber-500 transition-colors cursor-pointer">
              <Phone size={12} /> +234 000 000 0000
            </span>
            <span className="flex items-center gap-2 hover:text-amber-500 transition-colors cursor-pointer">
              <Mail size={12} /> info@ibadansouthanglican.org
            </span>
          </div>
          <div className="flex items-center gap-2 text-amber-500">
            <Sparkles size={10} />
            <span>Anglican Communion • Diocese of Ibadan South</span>
          </div>
        </div>
      </div>

      {/* Main Nav Container */}
      <nav className={`transition-all duration-500 ${scrolled ? 'bg-white/90 backdrop-blur-xl shadow-2xl py-2' : 'bg-white py-1'
        }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">

            {/* Elegant Logo Design */}
            <Link to="/" className="flex items-center gap-4 group">
              <div className="relative">
                <div className="w-12 h-12 rounded-2xl overflow-hidden shadow-lg border-2 border-white transition-transform group-hover:rotate-6">
                  {siteIcon ? (
                    <img src={siteIcon} alt="Logo" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-slate-800 to-slate-950 flex items-center justify-center text-amber-500 font-serif text-2xl">
                      ✝
                    </div>
                  )}
                </div>
                <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-amber-500 rounded-full border-2 border-white" />
              </div>
              <div className="flex flex-col">
                <span className="text-xl font-serif font-bold text-slate-900 tracking-tight leading-none mb-1">
                  Ibadan South
                </span>
                <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-amber-600">
                  Anglican Diocese
                </span>
              </div>
            </Link>

            {/* Desktop Menu - High End Typography */}
            <div className="hidden lg:flex items-center space-x-1">
              {navigationItems.map((item) => (
                <div
                  key={item.label}
                  className="relative px-1"
                  onMouseEnter={() => setOpenDropdown(item.label)}
                  onMouseLeave={() => setOpenDropdown(null)}
                >
                  <Link
                    to={item.path}
                    className={`flex items-center px-4 py-2 rounded-xl text-[11px] font-bold uppercase tracking-widest transition-all duration-300 ${isActive(item.path)
                      ? 'text-amber-600 bg-amber-50/50'
                      : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                      }`}
                  >
                    {item.label}
                    {item.dropdown && <ChevronDown className={`ml-2 h-3 w-3 transition-transform ${openDropdown === item.label ? 'rotate-180' : ''}`} />}
                  </Link>

                  {/* Enhanced Dropdown Menu */}
                  <AnimatePresence>
                    {item.dropdown && openDropdown === item.label && (
                      <motion.div
                        initial={{ opacity: 0, y: 10, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 10, scale: 0.95 }}
                        className="absolute left-0 mt-2 w-56 bg-white rounded-2xl shadow-2xl border border-slate-100 overflow-hidden py-2"
                      >
                        {item.dropdown.map((subItem) => (
                          <Link
                            key={subItem.label}
                            to={subItem.path}
                            className="block px-6 py-3 text-[11px] font-bold uppercase tracking-wider text-slate-500 hover:text-amber-600 hover:bg-amber-50/30 transition-all"
                          >
                            {subItem.label}
                          </Link>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}
            </div>

            {/* Mobile Menu Interaction */}
            <div className="lg:hidden flex items-center">
              <button
                onClick={() => setIsOpen(!isOpen)}
                className="p-3 rounded-2xl bg-slate-50 text-slate-900 hover:bg-amber-50 hover:text-amber-600 transition-all"
              >
                {isOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Drawer Overlay */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed inset-y-0 right-0 w-[85%] max-w-sm bg-white shadow-2xl z-[110] p-6 lg:hidden overflow-y-auto"
          >
            <div className="flex justify-between items-center mb-12">
              <span className="font-serif font-bold text-2xl">Menu</span>
              <button onClick={() => setIsOpen(false)} className="p-2 bg-slate-100 rounded-full">
                <X size={20} />
              </button>
            </div>

            <div className="space-y-4">
              {navigationItems.map((item) => (
                <div key={item.label} className="border-b border-slate-50 pb-2">
                  <div className="flex items-center justify-between">
                    <Link
                      to={item.path}
                      onClick={() => !item.dropdown && setIsOpen(false)}
                      className={`text-lg font-serif font-bold ${isActive(item.path) ? 'text-amber-600' : 'text-slate-900'}`}
                    >
                      {item.label}
                    </Link>
                    {item.dropdown && (
                      <button onClick={() => setOpenDropdown(openDropdown === item.label ? null : item.label)}>
                        <ChevronDown className={`transition-transform ${openDropdown === item.label ? 'rotate-180' : ''}`} />
                      </button>
                    )}
                  </div>

                  {item.dropdown && openDropdown === item.label && (
                    <motion.div className="pl-4 mt-4 space-y-4 border-l-2 border-amber-100">
                      {item.dropdown.map((sub) => (
                        <Link
                          key={sub.label}
                          to={sub.path}
                          onClick={() => setIsOpen(false)}
                          className="block text-sm font-bold text-slate-500 uppercase tracking-widest"
                        >
                          {sub.label}
                        </Link>
                      ))}
                    </motion.div>
                  )}
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navigation;